import { SystemEvent, SystemEventLevel } from 'bernie-logger';
import { ClientLogger } from 'bernie-client';
import { ExperimentStore } from 'bernie-plugin-mobx';
import { MarkPageUsableOptions } from 'bernie-plugin-mobx/dist/stores/common/analytics-store';
import { PlatformAnalyticsStore } from 'bernie-analytics';
import { PerformanceMetricsConfig } from 'bernie-analytics/dist/prime/platform-analytics-factory';
import { UisMicroMessage } from 'bernie-analytics/dist/prime/types';
import { ClientMetadataStore } from 'stores';
import { getBucketValueByExperimentName } from 'utility/experiment';

export enum CustomPerfMetricsType {
  CACHE_PAGE_USABLE = 'cachePageUsable',
  RESPONSE_SUMMARY_LIVE = 'responseSummaryLive',
  RESPONSE_SUMMARY_HYBRID = 'responseSummaryHybrid',
  RESPONSE_SUMMARY_CACHED_CONFIG = 'responseSummaryCachedConfig',
  RESPONSE_SUMMARY_CACHED_ML = 'responseSummaryCachedML',
  LISTINGS_PAGINATED_SLICED = 'listingsPaginatedSliced',
  LISTINGS_NON_SUPPLY_OVERHEAD = 'listingsNonSupplyOverhead',
  CUSTOMER_NOTIFICATION_FETCH_TIME = 'customerNotificationFetchTime',
  FLIGHTS_SEQUENTIAL_SEARCH = 'flightsSequentialSearch',
  FLIGHTS_INITIAL_SEARCH = 'flightsInitialSearch',
  FLIGHTS_SORT_OR_FILTER_SEARCH = 'flightsSortOrFilterSearch',
  FLIGHTS_FLEXIBLE_SEARCH = 'flightsFlexibleSearch',
  FLIGHTS_PRICE_SUMMARY = 'priceSummaryFetchTime',
  CACHE_SOURCE_BYOT_AIR_BLAZE_PUT = 'cacheSourceBYOTAirBlazePUT',
  CACHE_SOURCE_AIR_BLAZE_PUT = 'cacheSourceAirBlazePUT'
}

export enum ScopeType {
  LISTING = 'Listing',
  CUSTOMER_NOTIFICATION = 'Customer Notification',
  PRICE_SUMMARY = 'Price Summary'
}

type BerniePerformanceMetrics = PerformanceMetricsConfig & { microMessages: UisMicroMessage[] };

type CustomPerfMetricsMappingType = {
  [key in CustomPerfMetricsType]: {
    markValue?: string;
    logValue: string;
    enable: boolean;
    experimentName?: string;
    customPUT?: boolean;
    scope: string;
  };
};

type FlightsSearchType = {
  isSubsequentialSearch: boolean;
  isInitialSearch: boolean;
  isFlexibleSearch: boolean;
  isSortOrFilterApplied: boolean;
};

export const getCustomPerfMetricsMapping = (
  clientMetadataStore: ClientMetadataStore,
  experiment: ExperimentStore,
  flightsSearchType?: FlightsSearchType
): CustomPerfMetricsMappingType => ({
  cachePageUsable: {
    markValue: 'cachePageUsable',
    logValue: 'isCachedResponse',
    enable: Boolean(clientMetadataStore?.isCachedResponse),
    scope: ScopeType.LISTING
  },
  responseSummaryLive: {
    markValue: 'responseSummaryLiveMarkValue',
    logValue: 'responseSummaryLiveMarkValue',
    enable: Boolean(clientMetadataStore?.isResponseSummaryLive),
    scope: ScopeType.LISTING
  },
  responseSummaryHybrid: {
    markValue: 'responseSummaryHybridMarkValue',
    logValue: 'responseSummaryHybridMarkValue',
    enable: Boolean(clientMetadataStore?.isResponseSummaryHybrid),
    scope: ScopeType.LISTING
  },
  responseSummaryCachedConfig: {
    markValue: 'responseSummaryCachedConfigMarkValue',
    logValue: 'responseSummaryCachedConfigMarkValue',
    enable: Boolean(clientMetadataStore?.isResponseSummaryCachedConfig),
    scope: ScopeType.LISTING
  },
  responseSummaryCachedML: {
    markValue: 'responseSummaryCachedMLMarkValue',
    logValue: 'responseSummaryCachedMLMarkValue',
    enable: Boolean(clientMetadataStore?.isResponseSummaryCachedML),
    scope: ScopeType.LISTING
  },
  listingsPaginatedSliced: {
    markValue: 'listingsPaginatedSlicedMarkValue',
    logValue: 'listingsPaginatedSlicedMarkValue',
    enable: Boolean(clientMetadataStore?.isListingsPaginatedSliced),
    scope: ScopeType.LISTING
  },
  listingsNonSupplyOverhead: {
    markValue: 'listingsNonSupplyOverheadMarkValue',
    logValue: 'listingsNonSupplyOverheadMarkValue',
    enable: Boolean(clientMetadataStore?.supplyOverheadResponseMetricValue),
    customPUT: true,
    scope: ScopeType.LISTING
  },
  customerNotificationFetchTime: {
    logValue: 'isCustomerNotificationResponse',
    experimentName: 'Fshop_Travel_Advisory',
    enable: Boolean(getBucketValueByExperimentName(experiment, 'Fshop_Travel_Advisory')),
    scope: ScopeType.CUSTOMER_NOTIFICATION
  },
  priceSummaryFetchTime: {
    logValue: 'priceSummaryFetchTime',
    enable: true,
    scope: ScopeType.PRICE_SUMMARY
  },
  cacheSourceBYOTAirBlazePUT: {
    markValue: 'cacheSourceBYOTAirBlazePUT',
    logValue: 'cacheSourceBYOTAirBlazePUT',
    enable: Boolean(clientMetadataStore?.isCacheSourceBYOTAirBlaze),
    scope: ScopeType.LISTING
  },
  cacheSourceAirBlazePUT: {
    markValue: 'cacheSourceAirBlazePUT',
    logValue: 'cacheSourceAirBlazePUT',
    enable: Boolean(clientMetadataStore?.isCacheSourceAirBlaze),
    scope: ScopeType.LISTING
  },
  flightsSequentialSearch: {
    markValue: 'flightSequentialSearchPUT',
    logValue: 'flightSequentialSearchPUT',
    enable: Boolean(flightsSearchType?.isSubsequentialSearch),
    scope: ScopeType.LISTING
  },
  flightsInitialSearch: {
    markValue: 'flightsInitialSearchPUT',
    logValue: 'flightsInitialSearchPUT',
    enable: Boolean(flightsSearchType?.isInitialSearch),
    scope: ScopeType.LISTING
  },
  flightsSortOrFilterSearch: {
    markValue: 'flightsSortOrFilterSearchPUT',
    logValue: 'flightsSortOrFilterSearchPUT',
    enable: Boolean(flightsSearchType?.isSortOrFilterApplied),
    scope: ScopeType.LISTING
  },
  flightsFlexibleSearch: {
    markValue: 'flightsFlexibleSearchPUT',
    logValue: 'flightsFlexibleSearchPUT',
    enable: Boolean(flightsSearchType?.isFlexibleSearch),
    scope: ScopeType.LISTING
  }
});

const logger = ClientLogger.getLoggerWithIdentifier('utility.customPerfMetrics');
const PAGE_CHANGE = 'Page Change';

const getListingsCustomMetricsTypes = (
  customPerfMetricsMapping: CustomPerfMetricsMappingType
): CustomPerfMetricsType[] => {
  const customPerfMetricsTypeList: CustomPerfMetricsType[] = (Object.keys(
    customPerfMetricsMapping
  ) as CustomPerfMetricsType[]).filter(
    customPerfMetricsKey =>
      customPerfMetricsMapping[customPerfMetricsKey].enable &&
      customPerfMetricsMapping[customPerfMetricsKey].scope === ScopeType.LISTING
  );

  return customPerfMetricsTypeList;
};

const getPriceSummaryCustomMetricsTypes = (
  customPerfMetricsMapping: CustomPerfMetricsMappingType
): CustomPerfMetricsType[] => {
  const customPerfMetricsTypeList: CustomPerfMetricsType[] = (Object.keys(
    customPerfMetricsMapping
  ) as CustomPerfMetricsType[]).filter(
    customPerfMetricsKey =>
      customPerfMetricsMapping[customPerfMetricsKey].enable &&
      customPerfMetricsMapping[customPerfMetricsKey].scope === ScopeType.PRICE_SUMMARY
  );

  return customPerfMetricsTypeList;
};

const getCustomerNotificationsMetricsTypes = (
  customPerfMetricsMapping: CustomPerfMetricsMappingType
): CustomPerfMetricsType[] => {
  const customPerfMetricsTypeList: CustomPerfMetricsType[] = (Object.keys(
    customPerfMetricsMapping
  ) as CustomPerfMetricsType[]).filter(
    customPerfMetricsKey =>
      customPerfMetricsMapping[customPerfMetricsKey].enable &&
      customPerfMetricsMapping[customPerfMetricsKey].scope === ScopeType.CUSTOMER_NOTIFICATION
  );

  return customPerfMetricsTypeList;
};

const getListingsNonSupplyOverheadPageUsableTime = (
  pageUsableTime: number,
  clientMetadataStore: ClientMetadataStore
): number => {
  return pageUsableTime - clientMetadataStore.supplyOverheadResponseMetricValue;
};

const isListingsNonSupplyOverheadMark = (customPerfMetricsType: string) =>
  CustomPerfMetricsType.LISTINGS_NON_SUPPLY_OVERHEAD === customPerfMetricsType;

function findMark(markName: string): PerformanceEntry {
  if (typeof (performance || {}).getEntriesByType === 'function') {
    const origArr = performance.getEntriesByType('mark');
    const filteredArray = origArr.filter(sRow => sRow.name === markName);

    return filteredArray.length > 0 ? filteredArray[0] : undefined;
  }
}

function getPageUsableTime(
  markName: string,
  clientMetadataStore: ClientMetadataStore,
  customPerfMetricsType: string
): number {
  let pageUsableTime = (findMark(markName) || {}).startTime - ((findMark(PAGE_CHANGE) || {}).startTime || 0);
  if (isListingsNonSupplyOverheadMark(customPerfMetricsType)) {
    pageUsableTime = getListingsNonSupplyOverheadPageUsableTime(pageUsableTime, clientMetadataStore);
  }

  return Math.round(pageUsableTime * 10) / 10;
}

export function mark(name: string): void {
  if (typeof (performance || {}).mark === 'function') {
    performance.mark(name);
  }
}

function clearMarks(markName: string): void {
  if (typeof (performance || {}).clearMarks === 'function') {
    performance.clearMarks(markName);
  }
}

export function measure(measureName: string, startMark?: string, endMark?: string): void {
  if (typeof performance?.measure === 'function') {
    performance.measure(measureName, startMark, endMark);
  }
}

export function measurePageLoadTime() {
  if (document.readyState === 'complete') {
    measure('Page Load Time Measure', 'navigationStart', 'domContentLoadedEventEnd');
  } else {
    window.addEventListener('load', () => {
      measurePageLoadTime();
    });
  }
}

const getListingsNonSupplyOverheadMarkLogValue = (pageUsableInSec: number, logValue: string): string =>
  `${logValue}: ${pageUsableInSec}`;

export const getCommonPUTMetricsLogValue = (
  clientMetadataStore: ClientMetadataStore,
  experiment: ExperimentStore,
  customPerfMetricsMapping: CustomPerfMetricsMappingType
): string => {
  const commonPutMetricsLogValue = Object.values(customPerfMetricsMapping).reduce(
    (accumulator, customPerfMetrics, index) => {
      const { logValue, enable, experimentName, customPUT } = customPerfMetrics;
      if (customPUT) {
        return accumulator;
      }
      const prefix = `${index !== 0 ? ', ' : ''}${logValue}=`;
      if (!experimentName) {
        return `${accumulator}${prefix}${enable}`;
      }
      const experimentBucketValue = getBucketValueByExperimentName(experiment, experimentName);
      const value = `${Boolean(experimentBucketValue)}, experiment=${experimentName}, bucket=${experimentBucketValue}`;

      return `${accumulator}${prefix}${value}`;
    },
    ''
  );

  return commonPutMetricsLogValue;
};

export const getLogPerformanceTimingMetrics = (): string => {
  if (window?.performance?.timing) {
    const performanceTiming: PerformanceTiming = window.performance.timing;
    const currentPerformanceTiming: PerformanceTiming = performanceTiming.toJSON();
    const { navigationStart } = currentPerformanceTiming;

    return Object.keys(currentPerformanceTiming).reduce(
      (accumulator: string, performanceTimingKey: string, index: number) => {
        if (performanceTimingKey && typeof currentPerformanceTiming[performanceTimingKey] === 'number') {
          const prefix = index > 0 ? ', ' : '';
          const performanceTimingDifference =
            performanceTimingKey !== 'navigationStart' && typeof currentPerformanceTiming.navigationStart === 'number'
              ? currentPerformanceTiming[performanceTimingKey] - navigationStart
              : currentPerformanceTiming[performanceTimingKey];

          const relativePerformance = Math.max(0, performanceTimingDifference);
          const value = `${performanceTimingKey}=${relativePerformance}`;

          return `${accumulator}${prefix}${value}`;
        }

        return accumulator;
      },
      ''
    );
  }

  return '';
};

export const getNetworkLog = (): string => {
  if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
    const connection = navigator['connection'] || navigator['mozConnection'] || navigator['webkitConnection'];
    if (connection && connection.effectiveType) {
      return ', NetworkType: ' + connection.effectiveType;
    }
  }

  return '';
};

export const getEvaluatedExperimentsLog = (clientMetadataStore: ClientMetadataStore): string => {
  let log = '';
  if (
    clientMetadataStore?.clientMetadata?.evaluatedExperiments &&
    clientMetadataStore.clientMetadata.evaluatedExperiments.length
  ) {
    clientMetadataStore.clientMetadata.evaluatedExperiments.forEach(experiment => {
      log += `, ${experiment.id}=${experiment.bucket}`;
    });
  }

  return log;
};

export const getReferrerLog = (): string => {
  if (document && document.referrer) {
    return `, Referrer: ${document.referrer}`;
  }

  return '';
};

export const getBerniePerformanceMetricsLog = (): string => {
  const performanceMetrics: PerformanceMetricsConfig = PlatformAnalyticsStore.getPlatformAnalytics().getPerformanceMetrics();
  const berniePerformanceMetrics: UisMicroMessage = (performanceMetrics as BerniePerformanceMetrics).microMessages?.find(
    performanceMetric => performanceMetric.schemaName === 'performanceMetrics'
  );
  if (!berniePerformanceMetrics) {
    return '';
  }
  const { messageContent } = berniePerformanceMetrics;
  const { markName, markValue } = messageContent;
  const initialBerniePerformanceMetricsLogValue = `, Bernie Performance Metrics: ${markName}=${markValue}`;
  const customBerniePerformanceMetricKeys = Object.keys(messageContent).filter(
    berniePerformanceMetric => berniePerformanceMetric !== 'markName' && berniePerformanceMetric !== 'markValue'
  );

  const berniePerformanceMetricsLog = customBerniePerformanceMetricKeys.reduce(
    (accumulator, berniePerformancemetric) => {
      return `${accumulator}, ${berniePerformancemetric}=${messageContent[berniePerformancemetric]}`;
    },
    initialBerniePerformanceMetricsLogValue
  );

  return berniePerformanceMetricsLog;
};

/* contains only logValue for splunk */
/* istanbul ignore next */
const logCustomPUTMetrics = (
  pageUsableTime: number,
  pageName: string,
  clientMetadataStore: ClientMetadataStore,
  experiment: ExperimentStore,
  customPerfMetricsMapping: CustomPerfMetricsMappingType
): void => {
  const pageLogVal = `PageName: ${pageName || '-'}`;
  const putLogValue = `put: ${pageUsableTime}`;
  const commonPutMetricsLogValue = getCommonPUTMetricsLogValue(
    clientMetadataStore,
    experiment,
    customPerfMetricsMapping
  );
  const logPerformanceTimingMetrics = getLogPerformanceTimingMetrics();
  const listingsNonSupplyOverheadPageUsableTime = getListingsNonSupplyOverheadPageUsableTime(
    pageUsableTime,
    clientMetadataStore
  );
  const listingNonSupplyOverheaderMarkLogValue = getListingsNonSupplyOverheadMarkLogValue(
    listingsNonSupplyOverheadPageUsableTime,
    customPerfMetricsMapping.listingsNonSupplyOverhead.logValue
  );

  let logVal = `${pageLogVal}, ${putLogValue}, ${commonPutMetricsLogValue}, ${listingNonSupplyOverheaderMarkLogValue}`;
  logVal += getNetworkLog();
  logVal += getReferrerLog();
  logVal += getBerniePerformanceMetricsLog();

  logVal += `, Env=${clientMetadataStore?.env}`;
  logVal += `, SHA=${clientMetadataStore?.version}`;

  if (logPerformanceTimingMetrics) {
    logVal += `, PerformanceTiming: ${logPerformanceTimingMetrics}`;
  }
  logVal += getEvaluatedExperimentsLog(clientMetadataStore);
  logger.logEvent(new SystemEvent(SystemEventLevel.INFO, 'CUSTOM_PUT_METRICS'), logVal);
};

const initialiseCustomPerformanceMetrics = (
  pageName: string,
  clientMetadataStore: ClientMetadataStore,
  experiment: ExperimentStore,
  customPerfMetricsMapping: CustomPerfMetricsMappingType
): MarkPageUsableOptions => {
  const customPerformanceMetrics: MarkPageUsableOptions = {
    microMessages: [
      {
        schemaName: 'customPerfMetrics',
        messageContent: {
          markName: 'Flights Custom Metrics'
        }
      }
    ],
    callback: pageUsableTime => {
      logCustomPUTMetrics(pageUsableTime, pageName, clientMetadataStore, experiment, customPerfMetricsMapping);
    }
  };

  return customPerformanceMetrics;
};

export const getCustomMetrics = (
  customPerfMetricsTypeList: CustomPerfMetricsType[],
  pageName: string,
  clientMetadataStore: ClientMetadataStore,
  experiment: ExperimentStore,
  customPerfMetricsMapping: CustomPerfMetricsMappingType
): MarkPageUsableOptions => {
  try {
    if (!customPerfMetricsTypeList || customPerfMetricsTypeList.length == 0) {
      return null;
    }

    const customPerformanceMetrics = initialiseCustomPerformanceMetrics(
      pageName,
      clientMetadataStore,
      experiment,
      customPerfMetricsMapping
    );

    customPerfMetricsTypeList.forEach(customPerfMetricsType => {
      const { markValue } = customPerfMetricsMapping[customPerfMetricsType];
      const markName = markValue?.toString();
      if (markName) {
        clearMarks(markName);
        mark(markName);

        const pageUsableTime = getPageUsableTime(markName, clientMetadataStore, customPerfMetricsType);
        customPerformanceMetrics.microMessages[0].messageContent[markName] = pageUsableTime;
      }
    });

    return customPerformanceMetrics;
  } catch (e) {
    logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'GET_CUSTOM_PERF_METRICS'), e);
  }
};

export const getListingsCustomMetrics = (
  clientMetadataStore: ClientMetadataStore,
  experiment: ExperimentStore,
  flightsSearchType: FlightsSearchType
): MarkPageUsableOptions => {
  const customPerfMetricsMapping = getCustomPerfMetricsMapping(clientMetadataStore, experiment, flightsSearchType);
  const customPerfMetricsTypeList = getListingsCustomMetricsTypes(customPerfMetricsMapping);
  const rfrr = clientMetadataStore?.clientMetadata?.pageNameAnalytics.referrerId;
  const pageName = rfrr !== undefined ? rfrr : '-';
  const options = getCustomMetrics(
    customPerfMetricsTypeList,
    pageName,
    clientMetadataStore,
    experiment,
    customPerfMetricsMapping
  );

  return options;
};

export const getPriceSummaryCustomMetrics = (
  clientMetadataStore: ClientMetadataStore,
  experiment: ExperimentStore
): MarkPageUsableOptions => {
  const customPerfMetricsMapping = getCustomPerfMetricsMapping(clientMetadataStore, experiment);
  const customPerfMetricsTypeList = getPriceSummaryCustomMetricsTypes(customPerfMetricsMapping);
  const rfrr = clientMetadataStore?.clientMetadata?.pageNameAnalytics.referrerId;
  const pageName = rfrr !== undefined ? rfrr : '-';
  const options = getCustomMetrics(
    customPerfMetricsTypeList,
    pageName,
    clientMetadataStore,
    experiment,
    customPerfMetricsMapping
  );

  return options;
};

export const logCustomerNotificationsMetrics = (
  clientMetadataStore: ClientMetadataStore,
  experiment: ExperimentStore
): void => {
  try {
    const customPerfMetricsMapping = getCustomPerfMetricsMapping(clientMetadataStore, experiment);
    const customPerfMetricsTypeList = getCustomerNotificationsMetricsTypes(customPerfMetricsMapping);
    if (!customPerfMetricsTypeList?.length) {
      return null;
    }
    let logVal = '';
    let responseLogValue = '';
    let customMetricsLogValue = '';
    customPerfMetricsTypeList.forEach(customPerfMetricsType => {
      const { logValue, enable } = customPerfMetricsMapping[customPerfMetricsType];
      const value = clientMetadataStore.customerNotificationMetrics;
      responseLogValue += `Fetch time in ms: ${value}`;
      customMetricsLogValue += `${logValue}: ${enable}`;
    });
    logVal += `${responseLogValue}, ${customMetricsLogValue} ${getNetworkLog()} ${getReferrerLog()}`;
    logger.logEvent(new SystemEvent(SystemEventLevel.INFO, 'CUSTOMER_NOTIFICATION_METRICS'), logVal);
  } catch (e) {
    logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'CUSTOMER_NOTIFICATION_METRICS_FAILED'), e);
  }
};

export const logPrefetchMetrics = (time: number): void => {
  logger.logEvent(new SystemEvent(SystemEventLevel.INFO, 'SEAT_DETAILS_PREFETCH_METRICS'), time.toString());
};
